(() => {
  const factory = window.collectionFilters;

  if (typeof factory !== 'function') {
    console.warn('FAF collection return: collectionFilters was not available.');
    return;
  }

  const STATE_VERSION = 2;
  const STATE_PREFIX = 'faf:collection-position:v2:';
  const LEGACY_PREFIX = 'faf:collection-state:v1:';
  const STATE_LIFETIME = 30 * 60 * 1000;
  const MAX_RESTORE_PAGES = 12;
  const RETURN_MARKER_KEY = 'faf:collection-return-pending';

  const safeStorage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
      }
    }
  };

  const productCards = () => Array.from(document.querySelectorAll(
    '#productList [data-collection-product], ' +
    '#productList .faf-product-card-render[data-product-id]'
  ));

  const productHandle = (card) => {
    if (!card) return '';
    if (card.dataset.productHandle) return card.dataset.productHandle;

    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    try {
      const parts = new URL(link.href, window.location.origin).pathname.split('/products/');
      return String(parts[1] || '').split('/')[0].split('?')[0];
    } catch (error) {
      return '';
    }
  };

  const cardIdentity = (card) => ({
    id: String(card?.dataset?.productId || ''),
    handle: productHandle(card)
  });

  const sameIdentity = (card, identity = {}) => {
    if (!card) return false;

    const current = cardIdentity(card);

    if (identity.id && current.id === String(identity.id)) return true;
    return Boolean(identity.handle && current.handle === String(identity.handle));
  };

  const nearestVisibleCard = (cards) => {
    const firstVisible = cards.find((card) => card.getBoundingClientRect().bottom > 0);
    return firstVisible || cards[0] || null;
  };

  const waitForLayout = async () => {
    if (document.fonts?.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => window.setTimeout(resolve, 350))
      ]).catch(() => {});
    }

    await new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    });
  };

  const announce = (message) => {
    const live = document.querySelector('[data-collection-progress-live]');
    if (live) live.textContent = message;
  };

  const decorate = (component) => {
    const originalLoadMore = component.loadMore;
    const originalUpdateProgress = component.updateCollectionProgress;

    return {
      ...component,

      stateVersion: STATE_VERSION,
      stateLifetime: STATE_LIFETIME,
      stateCachePrefix: STATE_PREFIX,
      restoringPosition: false,
      savedProductAnchor: null,
      savedProductAnchorAt: 0,

      async init() {
        this.normaliseCurrentBrowserSaleHelpers();
        this.page = this.pageFromUrl();
        this.bindStateListeners();
        this.bindFilterInputListener();
        this.pruneCollectionStateCache();

        const restored = await this.restoreCollectionState();

        this.$nextTick(() => {
          this.updateCollectionProgress();

          if (!restored) {
            this.saveCollectionState();
          }
        });
      },

      collectionStateKey() {
        return `${STATE_PREFIX}${this.currentRelativeUrl()}`;
      },

      pruneCollectionStateCache() {
        try {
          const remove = [];

          for (let index = 0; index < window.sessionStorage.length; index += 1) {
            const key = window.sessionStorage.key(index);
            if (!key) continue;

            if (key.startsWith(LEGACY_PREFIX)) {
              remove.push(key);
              continue;
            }

            if (!key.startsWith(STATE_PREFIX)) continue;

            try {
              const payload = JSON.parse(window.sessionStorage.getItem(key) || '{}');
              const savedAt = Number(payload.savedAt || 0);

              if (!savedAt || Date.now() - savedAt > STATE_LIFETIME) {
                remove.push(key);
              }
            } catch (error) {
              remove.push(key);
            }
          }

          remove.forEach((key) => window.sessionStorage.removeItem(key));
        } catch (error) {
        }
      },

      bindStateListeners() {
        if (this.listenersBound) return;

        this.pageHideHandler = () => {
          const recentAnchor = (
            this.savedProductAnchor &&
            Date.now() - this.savedProductAnchorAt < 5 * 60 * 1000
          ) ? this.savedProductAnchor : null;

          this.saveCollectionState({ anchor: recentAnchor });
        };

        this.pageShowHandler = (event) => {
          if (!event.persisted) return;
          this.page = Math.max(this.page, this.pageFromUrl());
          this.updateCollectionProgress();
        };

        this.productClickHandler = (event) => {
          const target = event.target instanceof Element ? event.target : null;
          const productLink = target?.closest('#productList a[href*="/products/"]');
          const card = productLink?.closest(
            '[data-collection-product], .faf-product-card-render[data-product-id]'
          );

          if (!card) return;

          const anchor = this.buildProductAnchor(card);
          this.savedProductAnchor = anchor;
          this.savedProductAnchorAt = Date.now();
          this.saveCollectionState({ anchor, force: true });

          safeStorage.set(RETURN_MARKER_KEY, JSON.stringify({
            url: this.currentRelativeUrl(),
            savedAt: Date.now()
          }));
        };

        window.addEventListener('pagehide', this.pageHideHandler);
        window.addEventListener('pageshow', this.pageShowHandler);
        document.addEventListener('click', this.productClickHandler, true);
        this.listenersBound = true;
      },

      buildProductAnchor(card = null) {
        const cards = productCards();
        const target = card || nearestVisibleCard(cards);
        const index = Math.max(0, cards.indexOf(target));

        return {
          product: cardIdentity(target),
          index,
          viewportOffset: target ? target.getBoundingClientRect().top : 0,
          next: cards.slice(index + 1, index + 6).map(cardIdentity),
          previous: cards.slice(Math.max(0, index - 5), index).reverse().map(cardIdentity)
        };
      },

      saveCollectionState({
        scrollY = window.scrollY || window.pageYOffset || 0,
        force = false,
        anchor = null
      } = {}) {
        if (this.restoringPosition) return false;
        if (!force && (this.loading || this.loadingMore)) return false;

        const list = document.getElementById('productList');
        if (!list) return false;

        const state = {
          version: STATE_VERSION,
          savedAt: Date.now(),
          url: this.currentRelativeUrl(),
          page: Math.max(1, Number(this.page || 1)),
          scrollY: Math.max(0, Number(scrollY || 0)),
          mobileCols: Number(window.currentMobileCols || 2) === 1 ? 1 : 2,
          anchor: anchor || this.buildProductAnchor()
        };

        return safeStorage.set(this.collectionStateKey(), JSON.stringify(state));
      },

      readCollectionState() {
        try {
          const raw = safeStorage.get(this.collectionStateKey());
          if (!raw) return null;

          const state = JSON.parse(raw);
          const valid = (
            state &&
            state.version === STATE_VERSION &&
            state.url === this.currentRelativeUrl() &&
            Number.isFinite(Number(state.savedAt)) &&
            Date.now() - Number(state.savedAt) <= STATE_LIFETIME
          );

          if (!valid) {
            safeStorage.remove(this.collectionStateKey());
            return null;
          }

          return state;
        } catch (error) {
          safeStorage.remove(this.collectionStateKey());
          return null;
        }
      },

      async waitForCollectionIdle() {
        const startedAt = Date.now();

        while ((this.loading || this.loadingMore) && Date.now() - startedAt < 10000) {
          await new Promise((resolve) => window.setTimeout(resolve, 50));
        }
      },

      async loadToPage(targetPage) {
        const requestedPage = Math.min(
          MAX_RESTORE_PAGES,
          Math.max(1, Number(targetPage || 1))
        );

        while (this.page < requestedPage) {
          const pagination = document.getElementById('paginate');
          if (!pagination?.dataset?.nextUrl) break;

          await this.waitForCollectionIdle();
          const beforePage = this.page;
          await originalLoadMore.call(this);

          if (this.page <= beforePage) break;
        }
      },

      findRestorationTarget(anchor = {}) {
        const cards = productCards();
        if (!cards.length) return { target: null, originalMissing: false };

        const original = cards.find((card) => sameIdentity(card, anchor.product));
        if (original) return { target: original, originalMissing: false };

        for (const identity of anchor.next || []) {
          const target = cards.find((card) => sameIdentity(card, identity));
          if (target) return { target, originalMissing: true };
        }

        for (const identity of anchor.previous || []) {
          const target = cards.find((card) => sameIdentity(card, identity));
          if (target) return { target, originalMissing: true };
        }

        const index = Math.min(
          cards.length - 1,
          Math.max(0, Number(anchor.index || 0))
        );

        return { target: cards[index] || cards[0], originalMissing: true };
      },

      async positionAtTarget(target, viewportOffset, fallbackScrollY) {
        await waitForLayout();

        if (!target) {
          window.scrollTo(0, Math.max(0, Number(fallbackScrollY || 0)));
          return;
        }

        const desiredOffset = Number.isFinite(Number(viewportOffset))
          ? Number(viewportOffset)
          : 100;

        const position = () => {
          const top = window.scrollY + target.getBoundingClientRect().top - desiredOffset;
          window.scrollTo(0, Math.max(0, top));
        };

        position();

        window.setTimeout(() => {
          if (document.visibilityState === 'visible') position();
        }, 220);
      },

      async restoreCollectionState({ restoreScroll = true } = {}) {
        const state = this.readCollectionState();
        if (!state) return false;

        const navigationType = window.performance
          ?.getEntriesByType?.('navigation')?.[0]?.type || '';

        let pendingReturn = null;

        try {
          pendingReturn = JSON.parse(safeStorage.get(RETURN_MARKER_KEY) || 'null');
        } catch (error) {
          pendingReturn = null;
        }

        const referrerIsProduct = /\/products\//.test(document.referrer || '');
        const pendingMatches = Boolean(
          pendingReturn &&
          pendingReturn.url === this.currentRelativeUrl() &&
          Date.now() - Number(pendingReturn.savedAt || 0) <= STATE_LIFETIME
        );

        if (navigationType !== 'back_forward' && !(pendingMatches && referrerIsProduct)) {
          return false;
        }

        this.restoringPosition = true;

        try {
          if (
            Number(state.mobileCols) !== Number(window.currentMobileCols || 2) &&
            typeof window.switchGridMobileCols === 'function'
          ) {
            window.switchGridMobileCols(Number(state.mobileCols) === 1 ? 1 : 2);
          }

          await this.loadToPage(state.page);

          let result = this.findRestorationTarget(state.anchor || {});

          if (
            result.originalMissing &&
            document.getElementById('paginate')?.dataset?.nextUrl &&
            this.page < Math.min(MAX_RESTORE_PAGES, Number(state.page || 1) + 1)
          ) {
            await this.loadToPage(Number(state.page || 1) + 1);
            result = this.findRestorationTarget(state.anchor || {});
          }

          if (restoreScroll) {
            await this.positionAtTarget(
              result.target,
              state.anchor?.viewportOffset,
              state.scrollY
            );
          }

          this.page = Math.max(this.page, Number(state.page || 1));
          originalUpdateProgress.call(this);

          if (result.originalMissing && state.anchor?.product) {
            announce(
              'The item you viewed is no longer in this collection. ' +
              'You have been returned to the nearest available product.'
            );
          }

          safeStorage.remove(RETURN_MARKER_KEY);
          return true;
        } finally {
          this.restoringPosition = false;
        }
      }
    };
  };

  window.collectionFilters = (config) => decorate(factory(config));
})();